/**
 * Toast.jsx - Toast notification system using React Context.
 *
 * Usage:
 *   1. Wrap your app (or a subtree) with <ToastProvider>.
 *   2. In any descendant, call `const { toast } = useToast();`
 *   3. Invoke `toast("Saved successfully", "good")` to show a notification.
 *
 * Kinds:
 *   - "good"    -> green  (success)
 *   - "bad"     -> red    (error)
 *   - "warn"    -> yellow (warning)
 *   - undefined -> blue   (info / default)
 *
 * Toasts auto-dismiss after 4.6 seconds.
 */

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { X } from "lucide-react";

/* ------------------------------------------------------------------ */
/* Context                                                             */
/* ------------------------------------------------------------------ */

const ToastContext = createContext(null);

/* ------------------------------------------------------------------ */
/* Provider                                                            */
/* ------------------------------------------------------------------ */

let nextId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const dismiss = useCallback((id) => {
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message, kind) => {
      const id = ++nextId;
      setToasts((prev) => [...prev, { id, message, kind: kind || "default" }]);
      timersRef.current[id] = setTimeout(() => dismiss(id), 4600);
      return id;
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}

      {/* Toast container - fixed bottom-right */}
      <div style={styles.container} aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            style={{
              ...styles.toast,
              borderLeftColor: COLOR_MAP[t.kind]?.border ?? COLOR_MAP.default.border,
              background: COLOR_MAP[t.kind]?.bg ?? COLOR_MAP.default.bg,
              color: COLOR_MAP[t.kind]?.text ?? COLOR_MAP.default.text,
            }}
          >
            <span style={styles.message}>{t.message}</span>
            <button
              onClick={() => dismiss(t.id)}
              style={styles.closeBtn}
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
/* Hook                                                                */
/* ------------------------------------------------------------------ */

/**
 * useToast - returns { toast, dismiss }
 *   toast(message: string, kind?: "good" | "bad" | "warn" | "default") => number
 *   dismiss(id: number) => void
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a <ToastProvider>");
  }
  return ctx;
}

/* ------------------------------------------------------------------ */
/* Style helpers                                                       */
/* ------------------------------------------------------------------ */

const COLOR_MAP = {
  good: {
    border: "var(--good)",
    bg: "var(--good-soft)",
    text: "var(--good)",
  },
  bad: {
    border: "var(--bad)",
    bg: "var(--bad-soft)",
    text: "var(--bad)",
  },
  warn: {
    border: "var(--warn)",
    bg: "var(--warn-soft)",
    text: "var(--warn)",
  },
  default: {
    border: "var(--accent)",
    bg: "var(--info-soft)",
    text: "var(--accent)",
  },
};

const styles = {
  container: {
    position: "fixed",
    bottom: 20,
    right: 20,
    zIndex: 9999,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    maxWidth: 380,
  },
  toast: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    borderRadius: "var(--radius-sm)",
    borderLeft: "3px solid",
    boxShadow: "var(--shadow)",
    fontSize: 13,
    fontWeight: 550,
    animation: "toastIn 0.25s ease-out",
  },
  message: {
    flex: 1,
  },
  closeBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 2,
    opacity: 0.6,
    color: "inherit",
    display: "inline-flex",
  },
};
